/**
 * API Service for Gamifikasi AI-Itera
 * Handles all backend API calls
 */

import axios from 'axios';

// API Base URL - can be configured via environment variables
const API_BASE_URL = 'https://api-aiitera.itera.ac.id';

// Create axios instance with default config
const apiClient = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Request interceptor to add auth token
apiClient.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('token'); // Fixed: was 'authToken'
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Response interceptor to handle errors
apiClient.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      // Unauthorized - clear token and redirect to login
      localStorage.removeItem('token'); // Fixed: was 'authToken'
      localStorage.removeItem('user');
      window.location.href = '/login';
    }
    return Promise.reject(error);
  }
);

/**
 * Authentication API
 */
export const authAPI = {
  // Get Google login URL
  getGoogleLoginUrl: async () => {
    const response = await apiClient.get('/auth/google/login');
    return response.data;
  },

  // Handle Google OAuth callback
  handleGoogleCallback: async (code, state) => {
    const response = await apiClient.get('/auth/google/callback', {
      params: { code, state },
      withCredentials: true,
    });
    return response.data;
  },

  // Get current user info
  getCurrentUser: async () => {
    const response = await apiClient.get('/auth/me');
    return response.data;
  },

  // Logout
  logout: () => {
    localStorage.removeItem('token'); // Fixed: was 'authToken'
    localStorage.removeItem('user');
  },
};

/**
 * Leaderboard API
 */
export const leaderboardAPI = {
  // Get lecturer rankings
  getLecturers: async (limit = 100, campaign_id = null, search = null) => {
    const params = { limit };
    if (campaign_id) params.campaign_id = campaign_id;
    if (search) params.search = search;

    const response = await apiClient.get('/ranking/lecture', { params });
    return response.data;
  },

  // Get teaching staff rankings
  getTeachingStaff: async (limit = 100, campaign_id = null, search = null) => {
    const params = { limit };
    if (campaign_id) params.campaign_id = campaign_id;
    if (search) params.search = search;

    const response = await apiClient.get('/ranking/teaching-staff', { params });
    return response.data;
  },

  // Get student rankings
  getStudents: async (limit = 100, campaign_id = null, search = null) => {
    const params = { limit };
    if (campaign_id) params.campaign_id = campaign_id;
    if (search) params.search = search;

    const response = await apiClient.get('/ranking/student', { params });
    return response.data;
  },

  // Get study program rankings
  getStudyPrograms: async (limit = 100, campaign_id = null) => {
    const params = { limit };
    if (campaign_id) params.campaign_id = campaign_id;

    const response = await apiClient.get('/ranking/study-program', { params });
    return response.data;
  },

  // Get leaderboard summary/statistics
  getSummary: async () => {
    const response = await apiClient.get('/ranking/summary');
    return response.data;
  },

  // Search affiliates by NIM/NIP or name
  searchAffiliates: async (query, type = '', limit = 20) => {
    const response = await apiClient.get('/api/affiliates/search', {
      params: { q: query, type, limit },
    });
    return response.data;
  },
};

/**
 * Verification API
 */
export const verificationAPI = {
  // Get list of scraped data
  getScrapData: async (params = {}) => {
    const response = await apiClient.get('/scrap-data', { params });
    return response.data;
  },

  // Verify/approve content
  verifyContent: async (data) => {
    const response = await apiClient.post('/verification', data);
    return response.data;
  },

  // Submit user video
  submitUserVideo: async (data) => {
    const response = await apiClient.post('/api/user/submit-video', data);
    return response.data;
  },

  // Get user's uploaded videos
  getMyVideos: async (params = {}) => {
    const response = await apiClient.get('/api/user/my-videos', { params });
    return response.data;
  },
};

/**
 * Scraping API (Admin only)
 */
export const scrapingAPI = {
  // Trigger all scraping
  triggerAll: async (hashtag = '#ai-itera') => {
    const response = await apiClient.post('/api/scraping/trigger', { hashtag });
    return response.data;
  },

  // Trigger Google scraping
  triggerGoogle: async (hashtag = '#ai-itera') => {
    const response = await apiClient.post('/api/scraping/trigger-google', { hashtag });
    return response.data;
  },

  // Trigger YouTube scraping
  triggerYoutube: async (hashtag = '#ai-itera') => {
    const response = await apiClient.post('/api/scraping/trigger-youtube', { hashtag });
    return response.data;
  },

  // Get scraping logs
  getLogs: async (params = {}) => {
    const response = await apiClient.post('/api/scraping/logs', params);
    return response.data;
  },
};

/**
 * Campaign API
 */
export const campaignAPI = {
  // List all campaigns with optional filtering
  getAll: async (params = {}) => {
    const response = await apiClient.get('/api/campaigns', { params });
    return response.data;
  },

  // Create new campaign
  create: async (data) => {
    const response = await apiClient.post('/api/campaigns', data);
    return response.data;
  },

  // Get campaign by ID
  get: async (id) => {
    const response = await apiClient.get(`/api/campaigns/${id}`);
    return response.data;
  },

  // Update campaign
  update: async (id, data) => {
    const response = await apiClient.put(`/api/campaigns/${id}`, data);
    return response.data;
  },

  // Delete campaign
  delete: async (id) => {
    const response = await apiClient.delete(`/api/campaigns/${id}`);
    return response.data;
  },

  // Campaign Actions
  start: async (id) => {
    const response = await apiClient.post(`/api/campaigns/${id}/start`);
    return response.data;
  },

  pause: async (id) => {
    const response = await apiClient.post(`/api/campaigns/${id}/pause`);
    return response.data;
  },

  complete: async (id) => {
    const response = await apiClient.post(`/api/campaigns/${id}/complete`);
    return response.data;
  },

  run: async (id) => {
    const response = await apiClient.post(`/api/campaigns/${id}/run`);
    return response.data;
  },

  // Campaign Data
  getStats: async (id) => {
    const response = await apiClient.get(`/api/campaigns/${id}/stats`);
    return response.data;
  },

  getResults: async (id, params = {}) => {
    const response = await apiClient.get(`/api/campaigns/${id}/results`, { params });
    return response.data;
  },

  getLeaderboard: async (id) => {
    const response = await apiClient.get(`/api/campaigns/${id}/leaderboard`);
    return response.data;
  },
};

/**
 * Gamification Periods API
 */
export const gamificationPeriodAPI = {
  // List all gamification periods
  getAll: async () => {
    const response = await apiClient.get('/api/gamification-periods');
    return response.data;
  },

  // Get active period
  getActive: async () => {
    const response = await apiClient.get('/api/gamification-periods/active');
    return response.data;
  },

  // Get period by ID
  get: async (id) => {
    const response = await apiClient.get(`/api/gamification-periods/${id}`);
    return response.data;
  },

  // Create new period (Admin only)
  create: async (data) => {
    const response = await apiClient.post('/api/gamification-periods', data);
    return response.data;
  },

  // Update period (Admin only)
  update: async (id, data) => {
    const response = await apiClient.put(`/api/gamification-periods/${id}`, data);
    return response.data;
  },

  // Delete period (Admin only)
  delete: async (id) => {
    const response = await apiClient.delete(`/api/gamification-periods/${id}`);
    return response.data;
  },
};

/**
 * Certification API
 */
export const certificationAPI = {
  // Upload certification
  upload: async (formData) => {
    const response = await apiClient.post('/api/certifications/upload', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
    return response.data;
  },

  // Get list of certifications
  list: async (params = {}) => {
    const response = await apiClient.get('/api/certifications', { params });
    return response.data;
  },

  // Get single certification
  get: async (certId) => {
    const response = await apiClient.get(`/api/certifications/${certId}`);
    return response.data;
  },

  // Download certification PDF
  download: async (certId) => {
    const response = await apiClient.get(`/api/certifications/${certId}/download`, {
      responseType: 'blob',
    });
    return response.data;
  },

  // Delete certification
  delete: async (certId) => {
    const response = await apiClient.delete(`/api/certifications/${certId}`);
    return response.data;
  },

  // Verify certification (Admin only) - Manual NIM/NIP input (same as video)
  verify: async (certId, status, affiliate = null, notes = null) => {
    const payload = { status };
    if (affiliate) {
      payload.affiliate = affiliate;
    }
    if (notes) {
      payload.notes = notes;
    }
    const response = await apiClient.post(`/api/certifications/${certId}/verify`, payload);
    return response.data;
  },
};

/**
 * Helper function to handle API errors
 */
export const handleApiError = (error) => {
  if (error.response) {
    // Server responded with error status
    return {
      message: error.response.data?.message || 'An error occurred',
      status: error.response.status,
      data: error.response.data,
    };
  } else if (error.request) {
    // Request made but no response received
    return {
      message: 'No response from server. Please check your connection.',
      status: 0,
    };
  } else {
    // Something else happened
    return {
      message: error.message || 'An unexpected error occurred',
      status: -1,
    };
  }
};

// Export apiClient as both named and default export
export { apiClient };
export default apiClient;
